import { z } from "zod"

const missionTableSortSchema = z.object({
    field: z
        .enum([
            "aircraft",
            "flightTime",
            "maxAltitude",
            "maxSpeed",
            "numChecklistSubmissions",
            "started_at",
            "distanceFlown",
        ])
        .optional(),
    dir: z.enum(["ASC", "DESC"]).optional(),
})

const missionTableFilterBaseSchema = z.object({
    search: z.string().optional(),
    tags: z.array(z.string()).optional(),
    missingChecklist: z.boolean().optional(),
    userId: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
})

const missionTableFilterSchema = missionTableFilterBaseSchema.and(
    z.union([
        z.object({ aircraft: z.array(z.string()), unassignedAircraft: z.undefined().optional() }),
        z.object({ aircraft: z.undefined().optional(), unassignedAircraft: z.literal(true) }),
        z.object({
            aircraft: z.undefined().optional(),
            unassignedAircraft: z.undefined().optional(),
        }),
    ])
)

export const missionTablePayloadSchema = z.object({
    page: z.number().optional(),
    limit: z.number().optional(),
    sort: missionTableSortSchema.optional(),
    filter: missionTableFilterSchema.optional(),
})

export type MissionTablePayload = z.infer<typeof missionTablePayloadSchema>

const missionTableTagSchema = z.object({
    id: z.string(),
    name: z.string(),
})

export const missionTableMissionItemSchema = z.object({
    type: z.literal("mission"),
    id: z.string(),
    provider: z.string().nullable(),
    started_at: z
        .union([z.string(), z.date()])
        .transform((v) => (v instanceof Date ? v.toISOString() : v)),
    user_id: z.string().nullable(),
    pilot: z.object({ name: z.string(), id: z.string() }).nullable(),
    aircraft: z.object({ name: z.string().nullable(), id: z.string() }).nullable(),
    flightTime: z.coerce.number(),
    maxAltitude: z.coerce.number().nullable(),
    tags: z.array(missionTableTagSchema),
    distanceFlown: z.coerce.number().nullable(),
    numChecklistSubmissions: z.coerce.number(),
    maxSpeed: z.coerce.number().nullable(),
    linkedProjectIds: z.array(z.string()),
})

const missionTableGroupItemSchema = z.object({
    type: z.literal("group"),
    id: z.string(),
    name: z.string(),
    missions: z.array(missionTableMissionItemSchema),
    tags: z.array(missionTableTagSchema),
})

export const missionTableItemSchema = z.discriminatedUnion("type", [
    missionTableMissionItemSchema,
    missionTableGroupItemSchema,
])

type MissionTableItemType = "mission" | "group"
export type MissionTableItem<T extends MissionTableItemType = MissionTableItemType> = Extract<
    z.infer<typeof missionTableItemSchema>,
    { type: T }
>
